import { useSyncExternalStore } from "react";

/**
 * App theme. Dark is the default (and the design's home base); light mode is
 * a runtime remap of the palette variables — see the html.light block in
 * globals.css. The persisted choice is applied before first paint by the
 * inline script in index.html, so this module only needs to keep <html>'s
 * class, the theme-color meta, and subscribers in sync afterwards.
 */
export type Theme = "dark" | "light";

const STORAGE_KEY = "syncsuite.crm.theme.v1";
/** Matches --color-bg for each theme — keeps the browser chrome (mobile URL
 *  bar, PWA title bar) the same color as the page. */
const THEME_COLOR: Record<Theme, string> = { dark: "#09090b", light: "#fafafa" };

let current: Theme = readInitial();
const listeners = new Set<() => void>();

/** Prima Club: light is the default; dark remains available from the toggle. */
function readInitial(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function apply(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle("light", theme === "light");
  root.classList.toggle("dark", theme === "dark");
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLOR[theme]);
}

export function getTheme(): Theme {
  return current;
}

export function setTheme(theme: Theme) {
  if (theme === current) return;
  current = theme;
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* private mode */ }
  apply(theme);
  listeners.forEach((l) => l());
}

export function toggleTheme() {
  setTheme(current === "dark" ? "light" : "dark");
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive theme value — re-renders the caller when the theme flips. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, () => "light" as Theme);
}

// The index.html inline script only handles the `light` class; run apply()
// once on module load so the meta tag (and any missed class state) is
// consistent even before React mounts.
apply(current);

/* ── Chart colors ─────────────────────────────────────────────────────────
   Recharts/xyflow paint most colors as SVG presentation attributes, where
   var(--color-*) is invalid — so charts read their neutrals from here and
   re-render on theme change. Values mirror the zinc remap in globals.css. */

export interface ChartColors {
  /** Matches the card surface charts sit on (donut slice gaps, map strokes). */
  surface: string;
  /** Grid lines / dot-matrix backgrounds (zinc-800 ↔ zinc-200). */
  grid: string;
  /** Axis lines & faint decorative strokes (zinc-600 ↔ zinc-400 flavor). */
  axis: string;
  /** Tick label text (zinc-500 — legible on both themes). */
  tick: string;
  /** Secondary label text (zinc-400 ↔ zinc-600). */
  label: string;
  /** Tooltip panel background / border / text. */
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  /** Brand accent for area/line fills — rose-400 on dark, rose-500 on light. */
  accent: string;
}

const CHART_COLORS: Record<Theme, ChartColors> = {
  dark: {
    surface: "#18181b",
    grid: "#27272a",
    axis: "#52525b",
    tick: "#71717a",
    label: "#a1a1aa",
    tooltipBg: "#18181b",
    tooltipBorder: "#3f3f46",
    tooltipText: "#e4e4e7",
    accent: "#fb7185",
  },
  light: {
    surface: "#f4f4f5",
    grid: "#e4e4e7",
    axis: "#a1a1aa",
    tick: "#71717a",
    label: "#52525b",
    tooltipBg: "#ffffff",
    tooltipBorder: "#d4d4d8",
    tooltipText: "#27272a",
    accent: "#6366f1",
  },
};

export function chartColors(theme: Theme): ChartColors {
  return CHART_COLORS[theme];
}

/** Theme-reactive chart palette for recharts/xyflow SVG attributes. */
export function useChartColors(): ChartColors {
  return CHART_COLORS[useTheme()];
}
